import React, { useEffect, useState } from "react";
import Classes from "../Profile.module.css";

const phoneOk = (s = "") => {
  // at least 10 digits after stripping non-digits
  const digits = String(s).replace(/\D/g, "");
  return digits.length >= 10;
};

export default function LocationInfo({ basics, data, updateBasics, update, onNext }) {
  // ── Local, controlled copies
  const [formBasics, setFormBasics] = useState({
    firstName: basics?.firstName || "",
    lastName: basics?.lastName || "",
    phone: basics?.phone || "",
    phoneConsent: Boolean(basics?.phoneConsent),
  });
  const [formLoc, setFormLoc] = useState({
    address: data?.address || "",
    city: data?.city || "",
    state: data?.state || "",
    postalCode: data?.postalCode || "",
    neighborhood: data?.neighborhood || "",
    company: data?.company || "",
    gateCode: data?.gateCode || "",
    parkingSpot: data?.parkingSpot || "",
    notes: data?.notes || "",
  });

  // If parent props change, only update fields that actually changed.
  useEffect(() => {
    setFormBasics((prev) => ({
      ...prev,
      firstName: basics?.firstName ?? prev.firstName,
      lastName: basics?.lastName ?? prev.lastName,
      phone: basics?.phone ?? prev.phone,
      // preserve local consent unless parent explicitly provides it
      phoneConsent:
        typeof basics?.phoneConsent === "boolean" ? basics.phoneConsent : prev.phoneConsent,
    }));
  }, [basics]);

  useEffect(() => {
    setFormLoc((prev) => ({
      ...prev,
      address: data?.address ?? prev.address,
      city: data?.city ?? prev.city,
      state: data?.state ?? prev.state,
      postalCode: data?.postalCode ?? prev.postalCode,
      neighborhood: data?.neighborhood ?? prev.neighborhood,
      company: data?.company ?? prev.company,
      gateCode: data?.gateCode ?? prev.gateCode,
      parkingSpot: data?.parkingSpot ?? prev.parkingSpot,
      notes: data?.notes ?? prev.notes,
    }));
  }, [data]);

  // keep parent in sync with the full basics object (prevents “forgetting” phoneConsent)
  const pushBasicsUp = (next) => updateBasics?.(next);
  const pushLocUp = (partial) => update?.(partial);

  const setBasic = (name, value) => {
    const next = { ...formBasics, [name]: value };
    setFormBasics(next);
    pushBasicsUp(next);
  };
  const setField = (name, value) => {
    const next = { ...formLoc, [name]: value };
    setFormLoc(next);
    pushLocUp({ [name]: value });
  };

  // ── Validation state
  const [errors, setErrors] = useState({});
  const setError = (k, v) => setErrors((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formBasics.firstName.trim()) newErrors.firstName = "Please enter your first name.";
    if (!formBasics.lastName.trim()) newErrors.lastName = "Please enter your last name.";
    if (!formBasics.phone.trim()) newErrors.phone = "Please enter your phone number.";
    else if (!phoneOk(formBasics.phone)) newErrors.phone = "Please enter a valid phone (10+ digits).";
    if (!formBasics.phoneConsent) newErrors.phoneConsent = "Please agree to receive text messages.";
    if (!formLoc.address.trim()) newErrors.address = "Please enter your address.";
    if (!formLoc.city.trim()) newErrors.city = "Please enter your city.";
    if (!formLoc.state.trim()) newErrors.state = "Please enter your state/province.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      alert(Object.values(newErrors).join("\n"));
      return;
    }

    onNext?.();
  };

  return (
    <form className={Classes.infoCon} onSubmit={handleSubmit} noValidate>
      {/* ───── Basics ───── */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>First Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formBasics.firstName}
            onChange={(e) => setBasic("firstName", e.target.value)}
            placeholder="John"
            aria-invalid={!!errors.firstName}
            required
          />
          {errors.firstName && <p className={Classes.errorText}>{errors.firstName}</p>}
        </div>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Last Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formBasics.lastName}
            onChange={(e) => setBasic("lastName", e.target.value)}
            placeholder="Smith"
            aria-invalid={!!errors.lastName}
            required
          />
          {errors.lastName && <p className={Classes.errorText}>{errors.lastName}</p>}
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Phone</h5>
          <input
            type="tel"
            className={Classes.inputField}
            value={formBasics.phone}
            onChange={(e) => setBasic("phone", e.target.value)}
            placeholder="+1 (000) 000-0000"
            aria-invalid={!!errors.phone}
            required
          />
          {errors.phone && <p className={Classes.errorText}>{errors.phone}</p>}

          {/* Phone consent (checkbox OUTSIDE label to prevent double toggles) */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
            <input
              id="phoneConsent"
              type="checkbox"
              checked={!!formBasics.phoneConsent}
              onChange={(e) => setBasic("phoneConsent", e.target.checked)}
              aria-invalid={!!errors.phoneConsent}
              required
              style={{ marginTop: 3 }}
            />
            <label htmlFor="phoneConsent" style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
              By providing your phone number, you agree to receive text messages from Fuel Clubs
              about your delivery updates and account notifications. Msg &amp; data rates may apply.
            </label>
          </div>
          {errors.phoneConsent && <p className={Classes.errorText}>{errors.phoneConsent}</p>}
        </div>
      </div>

      {/* ───── Address (manual fields) ───── */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Address</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formLoc.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="123 Main St"
            aria-invalid={!!errors.address}
            required
          />
          {errors.address && <p className={Classes.errorText}>{errors.address}</p>}
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>City</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formLoc.city}
            onChange={(e) => setField("city", e.target.value)}
            placeholder="City"
            aria-invalid={!!errors.city}
            required
          />
          {errors.city && <p className={Classes.errorText}>{errors.city}</p>}
        </div>

        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>State/Province</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formLoc.state}
            onChange={(e) => setField("state", e.target.value)}
            placeholder="State / Province"
            aria-invalid={!!errors.state}
            required
          />
          {errors.state && <p className={Classes.errorText}>{errors.state}</p>}
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Postal Code</h5>
          <input
            type="text"
            className={Classes.inputField}
            value={formLoc.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
            placeholder="Postal Code"
          />
        </div>
      </div>

      {/* Optional fields */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <div className={"flexAlign10"}>
            <h5 className={Classes.label}>Neighborhood</h5>
            <span className={"optionalText"}><h5>Optional</h5></span>
          </div>
          <input
            type="text"
            className={Classes.inputField}
            placeholder="Enter neighborhood"
            value={formLoc.neighborhood}
            onChange={(e) => setField("neighborhood", e.target.value)}
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <div className={"flexAlign10"}>
            <h5 className={Classes.label}>Company</h5>
            <span className={"optionalText"}><h5>Optional</h5></span>
          </div>
          <input
            type="text"
            className={Classes.inputField}
            placeholder="Enter your company"
            value={formLoc.company}
            onChange={(e) => setField("company", e.target.value)}
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <div className={"flexAlign10"}>
            <h5 className={Classes.label}>Gate Code/Access Instructions</h5>
            <span className={"optionalText"}><h5>Optional</h5></span>
          </div>
          <input
            type="text"
            className={Classes.inputField}
            placeholder="Enter your gate code"
            value={formLoc.gateCode}
            onChange={(e) => setField("gateCode", e.target.value)}
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <div className={"flexAlign10"}>
            <h5 className={Classes.label}>Parking Spot # or Detailed Location Notes</h5>
            <span className={"optionalText"}><h5>Optional</h5></span>
          </div>
          <input
            type="text"
            className={Classes.inputField}
            placeholder="Enter your parking spot #"
            value={formLoc.parkingSpot}
            onChange={(e) => setField("parkingSpot", e.target.value)}
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <div className={Classes.textAreaCon}>
            <textarea
              className={Classes.textArea}
              rows={5}
              placeholder="Additional notes (optional)"
              value={formLoc.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </div>
        </div>
      </div>

      <button type="submit" className={Classes.saveBtn}><h5>Next</h5></button>
    </form>
  );
}